import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Loader2, Plus } from 'lucide-react';

import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Separator } from '../../components/ui/separator';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * B2BCreateOrderTab — Dashboard tab for B2B managers to create new pass
 * orders for business clients. Supports volume tiers (staffel pricing).
 *
 * Extracted from App.js (Fase 2) — pure relocation, no logic changes.
 */
export const B2BCreateOrderTab = ({ user }) => {
  const [submitting, setSubmitting] = useState(false);
  const [tiers, setTiers] = useState(null);
  const [formData, setFormData] = useState({
    business_name: '',
    business_address: '',
    business_email: '',
    business_phone: '',
    vat_number: '',
    pass_type: 'annual',
    quantity: 1,
    notes: ''
  });

  // Fetch staffel pricing tiers
  useEffect(() => {
    const fetchTiers = async () => {
      try {
        const res = await axios.get(`${API}/b2b/pricing-tiers`);
        setTiers(res.data);
      } catch (e) {
        console.error('Failed to load pricing tiers');
      }
    };
    fetchTiers();
  }, []);

  // Calculate tier price based on pass_type + quantity
  const getTierPrice = (passType, qty) => {
    if (passType === 'free_annual') return { price: 0, onRequest: false };
    const tierList = tiers?.[passType] || [];
    for (const t of tierList) {
      if (t.max_qty === null && qty >= t.min_qty) return { price: null, onRequest: true };
      if (qty >= t.min_qty && qty <= t.max_qty) return { price: t.price, onRequest: false };
    }
    return { price: 0, onRequest: false };
  };

  const tierResult = getTierPrice(formData.pass_type, formData.quantity);
  const unitPrice = tierResult.onRequest ? 0 : (tierResult.price ?? 0);
  const isOnRequest = tierResult.onRequest;
  const isFreeAnnual = formData.pass_type === 'free_annual';

  const orderSubtotal = unitPrice * formData.quantity;
  const orderBtw = Math.round(orderSubtotal * 0.21 * 100) / 100;
  const orderTotalInclBtw = Math.round((orderSubtotal + orderBtw) * 100) / 100;

  // Force quantity to 1 for free_annual
  const handlePassTypeChange = (v) => {
    setFormData({
      ...formData,
      pass_type: v,
      quantity: v === 'free_annual' ? 1 : formData.quantity
    });
  };

  const handleQuantityChange = (val) => {
    const qty = Math.max(1, parseInt(val) || 1);
    setFormData({ ...formData, quantity: isFreeAnnual ? 1 : qty });
  };

  const handleSubmitOrder = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const response = await axios.post(`${API}/b2b/orders`, {
        ...formData,
        unit_price: unitPrice
      }, {
        headers: { Authorization: `Bearer ${localStorage.getItem('freestays_token')}` }
      });

      if (response.data.success) {
        const msg = isOnRequest
          ? 'Offerte aanvraag ingediend voor 1001+ stuks. U wordt zo spoedig mogelijk gecontacteerd.'
          : 'B2B order ingediend! De klant ontvangt een bevestigingsmail.';
        toast.success(msg);
        setFormData({
          business_name: '',
          business_address: '',
          business_email: '',
          business_phone: '',
          vat_number: '',
          pass_type: 'annual',
          quantity: 1,
          notes: ''
        });
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Order kon niet worden ingediend');
    } finally {
      setSubmitting(false);
    }
  };

  // Build staffel preview for current pass type
  const currentTiers = (!isFreeAnnual && tiers?.[formData.pass_type]) || [];

  return (
    <div className="space-y-6">
      <div>
        <h2 className="font-bold text-2xl">Create Order</h2>
        <p className="text-muted-foreground">Nieuwe B2B pass order aanmaken voor een klant</p>
      </div>

      <Card className="rounded-2xl">
        <CardContent className="p-6">
          <form onSubmit={handleSubmitOrder} className="space-y-5">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label>Business Name *</Label>
                <Input
                  value={formData.business_name}
                  onChange={(e) => setFormData({ ...formData, business_name: e.target.value })}
                  placeholder="Bedrijfsnaam"
                  required
                  data-testid="create-order-business-name"
                />
              </div>
              <div>
                <Label>VAT Number *</Label>
                <Input
                  value={formData.vat_number}
                  onChange={(e) => setFormData({ ...formData, vat_number: e.target.value })}
                  placeholder="NL123456789B01"
                  required
                  data-testid="create-order-vat"
                />
              </div>
            </div>
            <div>
              <Label>Business Address *</Label>
              <Textarea
                value={formData.business_address}
                onChange={(e) => setFormData({ ...formData, business_address: e.target.value })}
                placeholder="Straat, Stad, Postcode, Land"
                rows={2}
                required
                data-testid="create-order-address"
              />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label>Contact Email *</Label>
                <Input
                  type="email"
                  value={formData.business_email}
                  onChange={(e) => setFormData({ ...formData, business_email: e.target.value })}
                  required
                  data-testid="create-order-email"
                />
              </div>
              <div>
                <Label>Phone *</Label>
                <Input
                  value={formData.business_phone}
                  onChange={(e) => setFormData({ ...formData, business_phone: e.target.value })}
                  placeholder="+31 6 1234 5678"
                  required
                  data-testid="create-order-phone"
                />
              </div>
            </div>
            <Separator />
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <Label>Pass Type</Label>
                <Select value={formData.pass_type} onValueChange={handlePassTypeChange}>
                  <SelectTrigger data-testid="create-order-pass-type">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="one_time">One-Time Pass</SelectItem>
                    <SelectItem value="annual">Annual Pass</SelectItem>
                    <SelectItem value="free_annual">Free Annual Pass</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Quantity</Label>
                <Input
                  type="number"
                  min="1"
                  max={isFreeAnnual ? 1 : undefined}
                  value={formData.quantity}
                  onChange={(e) => handleQuantityChange(e.target.value)}
                  disabled={isFreeAnnual}
                  data-testid="create-order-quantity"
                />
              </div>
              <div>
                <Label>Stukprijs excl. BTW</Label>
                {isOnRequest ? (
                  <div className="h-10 flex items-center px-3 rounded-md border bg-secondary/50 text-amber-500 font-medium">Op aanvraag</div>
                ) : (
                  <div className="h-10 flex items-center px-3 rounded-md border bg-secondary/30 font-medium" data-testid="create-order-unit-price">
                    €{unitPrice.toFixed(2)}
                  </div>
                )}
              </div>
            </div>

            {/* Staffel preview */}
            {currentTiers.length > 0 && (
              <div className="text-xs text-muted-foreground bg-secondary/30 p-3 rounded-lg">
                <p className="font-medium mb-1">Staffelprijzen (excl. BTW):</p>
                <div className="flex flex-wrap gap-x-4 gap-y-1">
                  {currentTiers.map((t, i) => (
                    <span key={i} className={formData.quantity >= t.min_qty && (t.max_qty === null || formData.quantity <= t.max_qty) ? 'text-[#00A9E0] font-semibold' : ''}>
                      {t.max_qty === null ? `${t.min_qty}+` : `${t.min_qty}-${t.max_qty}`}: {t.price === null ? 'Op aanvraag' : `€${t.price.toFixed(2)}`}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {!isOnRequest && (
              <div className="bg-secondary/50 p-4 rounded-lg space-y-1">
                <div className="flex justify-between text-sm text-muted-foreground">
                  <span>Subtotaal ({formData.quantity}× €{unitPrice.toFixed(2)})</span>
                  <span>€{orderSubtotal.toFixed(2)}</span>
                </div>
                <div className="flex justify-between text-sm text-muted-foreground">
                  <span>BTW 21%</span>
                  <span>€{orderBtw.toFixed(2)}</span>
                </div>
                <div className="flex justify-between font-bold pt-1 border-t border-border">
                  <span>Totaal incl. BTW</span>
                  <span className="text-[#00A9E0]">€{orderTotalInclBtw.toFixed(2)}</span>
                </div>
                <p className="text-xs text-muted-foreground pt-1">Klant ontvangt een bevestigingsmail. Na bevestiging wordt de factuur automatisch verzonden.</p>
              </div>
            )}

            {isOnRequest && (
              <div className="bg-amber-500/10 border border-amber-500/30 p-4 rounded-lg text-center">
                <p className="text-amber-500 font-medium">1001+ stuks: prijs op aanvraag</p>
                <p className="text-sm text-muted-foreground mt-1">Na het indienen van de offerte aanvraag nemen wij contact op.</p>
              </div>
            )}

            <div>
              <Label>Notes (Optional)</Label>
              <Textarea
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                placeholder="Eventuele opmerkingen..."
                rows={2}
                data-testid="create-order-notes"
              />
            </div>
            <div className="flex justify-end pt-2">
              <Button type="submit" disabled={submitting} className={`rounded-full px-8 ${isOnRequest ? 'bg-amber-500 hover:bg-amber-600' : ''}`} data-testid="submit-create-order">
                {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Plus className="w-4 h-4 mr-2" />}
                {isOnRequest ? 'Offerte Aanvragen' : 'Submit Order'}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

export default B2BCreateOrderTab;
